<script setup lang="ts">
import { ref, onBeforeMount, computed } from 'vue';
import i18n from '@/i18n';
import { STATUS_OPEN, STATUS_CLOSED } from '@/constants';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useItemConfigurationStore } from '@/store/ItemConfigurationStore';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useToast } from 'primevue/usetoast';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import ProgressSpinner from 'primevue/progressspinner';
import ManageSignatureModal from '@/components/modal/ManageSignatureModal.vue';

const toast = useToast();
const dialog = useDialog();
const loading = ref(false);
const configurationModule = useItemConfigurationStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);

const filters = ref();
const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    imgUrl: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    sts: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFilters();

const signatures = computed(() => configurationModule.signatures);

const clearFilter = () => {
  initFilters();
};

const getSeverity = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
  }
};

const onUpdateStatus = async (sk, status) => {
  try {
    await configurationModule.updateSignatureStatus(sk, status);
    toast.add({ severity: 'success', summary: '', detail: i18n.global.t('notification.save_successfully'), life: 3000 });
  } catch (error) {
    toast.add({ severity: 'error', summary: '', detail: i18n.global.t('notification.save_error'), life: 5000 });
  }
};

const handleDelete = async (sk) => {
  await configurationModule
    .deleteSignatureBy(sk)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: i18n.global.t('voucher.notification.deleteSuccess'), life: 3000 });
      configurationModule.getAllSignature();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: i18n.global.t('voucher.notification.deleteFailure'), life: 5000 });
    });
};

const manageSignature = (data, message) => {
  const dialogRef = dialog.open(ManageSignatureModal, {
    props: {
      header: message,
      modal: true,
      maximizable: true,
      data
    },
    emits: {
      isSave: () => {
        dialogRef.close();
        configurationModule.getAllSignature();
      }
    }
  });
};

onBeforeMount(async () => {
  loading.value = true;
  await configurationModule.getAllSignature().finally(() => {
    loading.value = false;
  });
});
</script>
<template>
  <div class="card">
    <h5>{{ $t('voucher.signatureManagement.ftv_signature') }}</h5>
    <DataTable v-model:filters="filters" :value="signatures" paginator showGridlines :loading="loading" :rows="10" dataKey="id" filterDisplay="menu" :globalFilterFields="['code', 'name', 'logo', 'sts']">
      <template #header>
        <div class="flex justify-content-between flex-col sm:flex-row">
          <div class="flex justify-content-left flex-col sm:flex-row gap-3">
            <Button type="button" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="p-button-secondary mb-2" @click="clearFilter()" />
          </div>
          <div class="justify-content-right">
            <span class="p-input-icon-left mb-2">
              <i class="pi pi-search" />
              <InputText v-model="filters['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
            </span>
          </div>
        </div>
      </template>
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <template #loading>
        <ProgressSpinner class="w-4rem h-4rem" strokeWidth="8" animationDuration="2s" />
      </template>
      <TableColumn field="name" :header="$t('voucher.signatureManagement.name')" class="break-word">
        <template #body="{ data }">
          <span class="long-text" v-tooltip="data.name">
            {{ data.name }}
          </span>
        </template>
        <template #filter="{ filterModel }"> <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('voucher.signatureManagement.name')" /> </template
      ></TableColumn>
      <TableColumn field="imgUrl" :header="$t('voucher.signatureManagement.image')">
        <template #body="{ data }">
          <img :src="data.imgUrl" :alt="data.imgUrl" class="shadow-4 width-200px" />
        </template>
      </TableColumn>
      <TableColumn field="sts" :header="$t('voucher.signatureManagement.status')" v-if="authorizations?.configurationPage?.configurable_items_edit">
        <template #body="{ data }">
          <ToggleSwitch v-model="data.sts" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="onUpdateStatus(data.id, data.sts)" />
        </template>
        <template #filter="{ filterModel }">
          <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('management.air.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
            </template>
          </Select>
        </template>
      </TableColumn>
      <TableColumn :header="$t('common.action')" v-if="authorizations?.configurationPage?.configurable_items_edit">
        <template #body="{ data }">
          <div class="flex">
            <Button icon="pi pi-pencil" class="p-button-text" @click="manageSignature(data, i18n.global.t('voucher.signatureManagement.update'))" />
            <Button icon="pi pi-trash" class="p-button-text" @click="handleDelete(data.id)" />
          </div>
        </template>
      </TableColumn>
    </DataTable>
    <div>
      <Button
        v-if="authorizations?.configurationPage?.configurable_items_edit"
        id="signature-add-btn"
        :label="$t('voucher.signatureManagement.add')"
        icon="pi pi-plus"
        class="p-button-text"
        @click="manageSignature(undefined, i18n.global.t('voucher.signatureManagement.add'))"
      />
    </div>
  </div>
</template>
